/**
 * 회원 알림 설정 (센터별 수강권 만료 안내 발송 설정) — 타입과 순수 함수.
 *
 * 설계 정본: rappo 워크트리 `docs/CENTER_MEMBER_EXPIRY_ALERT_SETTING.md`
 *   - 4-2 API 계약, 7-4 데이터 규칙, 7-5 상태별 동작
 *
 * 이 파일은 화면을 모른다. 모달(UI)은 여기 함수의 결과만 그린다.
 * 문구는 `memberNotificationSettingsCopy.ts` 한 곳에 있다.
 */
import { MEMBER_NOTIFICATION_STATUS_COPY, MEMBER_NOTIFICATION_TIMING_COPY } from './memberNotificationSettingsCopy';

// ─────────────────────────────────────────────────────────────
// 키
// ─────────────────────────────────────────────────────────────

/** 알림 종류. 서버 응답·PATCH 본문의 최상위 키와 같다. */
export const MEMBER_NOTIFICATION_KINDS = ['membershipExpiryNotice', 'memberTicketExpiryNotice'] as const;
export type MemberNotificationKind = typeof MEMBER_NOTIFICATION_KINDS[number];

/**
 * 종류별 시점 키 — **표시 순서**이기도 하다.
 *
 * 서버 상수와 이름이 같아야 한다:
 *   rappo `src/constants/memberExpiryNotice.constant.ts` 의 `MEMBER_EXPIRY_NOTICE_TIMINGS`
 *   (설계 문서 3-2. membership = PT, memberTicket = 그룹)
 *
 * 서버가 모르는 키를 PATCH 에 섞으면 Joi 가 **요청 전체를 400** 으로 거절한다(4-4).
 * 그래서 새 키를 여기 추가하기 전에 서버 상수에 있는지 먼저 확인한다.
 */
export const MEMBER_NOTIFICATION_TIMING_KEYS = {
    membershipExpiryNotice: ['oneThirdLeft', 'oneSixthLeft', 'sevenDaysBefore'],
    memberTicketExpiryNotice: ['sevenDaysBefore', 'threeDaysBefore', 'oneDayBefore'],
} as const;

export type MembershipExpiryNoticeTiming = typeof MEMBER_NOTIFICATION_TIMING_KEYS.membershipExpiryNotice[number];
export type MemberTicketExpiryNoticeTiming = typeof MEMBER_NOTIFICATION_TIMING_KEYS.memberTicketExpiryNotice[number];
export type MemberNotificationTiming<K extends MemberNotificationKind = MemberNotificationKind> =
    typeof MEMBER_NOTIFICATION_TIMING_KEYS[K][number];

// ─────────────────────────────────────────────────────────────
// 타입
// ─────────────────────────────────────────────────────────────

/**
 * 시점 키 → 켜짐 여부.
 * 키를 `string` 으로 둔 이유: 서버가 이 화면이 모르는 키를 더 보낼 수 있다.
 * 그런 키는 행으로 그리지도, PATCH 에 싣지도 않는다(7-4).
 */
export type MemberNotificationTimingFlags = Record<string, boolean>;

/** 서버 상태 (GET 200 / PATCH 200 응답을 해석한 것) */
export type MemberNotificationSettings = {
    membershipExpiryNotice: MemberNotificationTimingFlags;
    memberTicketExpiryNotice: MemberNotificationTimingFlags;
    /** ISO 문자열. 한 번도 저장한 적 없으면 null */
    updatedAt: string | null;
};

/** 모달 안에서 편집 중인 값. 서버 상태로 초기화한다. */
export type MemberNotificationSettingsDraft = {
    membershipExpiryNotice: MemberNotificationTimingFlags;
    memberTicketExpiryNotice: MemberNotificationTimingFlags;
};

/** PATCH 본문 — **바꾼 키만** 담는다(4-2). 빈 섹션은 넣지 않는다(Joi `.min(1)`). */
export type MemberNotificationSettingsPatchBody = Partial<Record<MemberNotificationKind, MemberNotificationTimingFlags>>;

/** GET/PATCH 200 응답 본문 (4-2) */
export type MemberNotificationSettingsResponse = {
    success: boolean;
    memberNotificationSettings: {
        membershipExpiryNotice: MemberNotificationTimingFlags;
        memberTicketExpiryNotice: MemberNotificationTimingFlags;
        updatedAt: string | null;
    };
};

/**
 * API 함수의 반환 형태 (규칙 16).
 * 성공이든 실패든 **던지지 않고** 이 모양으로 돌려준다.
 * - 응답이 없는 네트워크 오류: `status: 0`
 * - 요청을 보내지 않고 막은 경우(아래 `clientBlocked`): `status: 0` + `data.clientBlocked`
 */
export type MemberNotificationSettingsApiResult = {
    status: number;
    data: any;
};

// ─────────────────────────────────────────────────────────────
// 응답 해석
// ─────────────────────────────────────────────────────────────

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

/** 불리언 값만 남긴다. 계약 위반 값(null·문자열 등)은 행으로 그리지 않는다. */
const pickBooleanFlags = (value: unknown): MemberNotificationTimingFlags | null => {
    if (!isPlainObject(value)) return null;
    const flags: MemberNotificationTimingFlags = {};
    Object.keys(value).forEach((key) => {
        if (typeof value[key] === 'boolean') flags[key] = value[key] as boolean;
    });
    return flags;
};

/**
 * 200 응답 본문 → 서버 상태. 모양이 계약과 다르면 **null** 이다.
 *
 * null 을 기본값(전부 켜짐)으로 메우지 않는다 — 설계 E10: 그리지 않는다.
 * 호출부는 null 을 "불러오지 못했어요" 로 다룬다.
 */
export const parseMemberNotificationSettings = (responseData: unknown): MemberNotificationSettings | null => {
    if (!isPlainObject(responseData)) return null;
    const raw = responseData.memberNotificationSettings;
    if (!isPlainObject(raw)) return null;

    const membershipExpiryNotice = pickBooleanFlags(raw.membershipExpiryNotice);
    const memberTicketExpiryNotice = pickBooleanFlags(raw.memberTicketExpiryNotice);
    if (!membershipExpiryNotice || !memberTicketExpiryNotice) return null;

    return {
        membershipExpiryNotice,
        memberTicketExpiryNotice,
        updatedAt: typeof raw.updatedAt === 'string' ? raw.updatedAt : null,
    };
};

// ─────────────────────────────────────────────────────────────
// 행으로 그릴 키 (7-4: GET 응답 키 ∩ 문구 사전 키)
// ─────────────────────────────────────────────────────────────

const hasTimingCopy = (kind: MemberNotificationKind, timing: string) =>
    Object.prototype.hasOwnProperty.call(MEMBER_NOTIFICATION_TIMING_COPY[kind], timing);

/**
 * 이 종류에서 **행으로 그릴 시점 키**, 표시 순서대로.
 *
 * 응답에 불리언으로 온 키 ∩ 문구 사전에 있는 키.
 *   - 응답에만 있는 키(서버가 새로 추가) → 문구가 없으니 그리지 않는다
 *   - 사전에만 있는 키(구서버라 응답에 없음) → 기본값을 지어내지 않고 그리지 않는다
 *
 * 초안·PATCH·종류 상태 계산이 전부 이 목록 위에서만 돈다 — 화면에 보이지 않는 키를
 * 몰래 바꾸거나 상태 배지에 섞지 않기 위해서다.
 */
export const getRenderableTimings = (
    settings: Pick<MemberNotificationSettings, MemberNotificationKind> | null | undefined,
    kind: MemberNotificationKind,
): MemberNotificationTiming[] => {
    const flags = settings?.[kind];
    if (!flags) return [];
    return (MEMBER_NOTIFICATION_TIMING_KEYS[kind] as readonly MemberNotificationTiming[]).filter(
        (timing) => typeof flags[timing] === 'boolean' && hasTimingCopy(kind, timing),
    );
};

// ─────────────────────────────────────────────────────────────
// 초안
// ─────────────────────────────────────────────────────────────

/** 서버 상태로 초안을 만든다. 그릴 수 있는 키만 담는다. */
export const createMemberNotificationSettingsDraft = (
    settings: MemberNotificationSettings,
): MemberNotificationSettingsDraft => {
    const draft: MemberNotificationSettingsDraft = { membershipExpiryNotice: {}, memberTicketExpiryNotice: {} };
    MEMBER_NOTIFICATION_KINDS.forEach((kind) => {
        getRenderableTimings(settings, kind).forEach((timing) => {
            draft[kind][timing] = settings[kind][timing];
        });
    });
    return draft;
};

/** 시점 하나를 바꾼 새 초안. 그릴 수 없는 키면 초안을 그대로 돌려준다. */
export const setDraftTiming = (
    draft: MemberNotificationSettingsDraft,
    settings: MemberNotificationSettings,
    kind: MemberNotificationKind,
    timing: string,
    value: boolean,
): MemberNotificationSettingsDraft => {
    if (!(getRenderableTimings(settings, kind) as string[]).includes(timing)) return draft;
    return { ...draft, [kind]: { ...draft[kind], [timing]: value } };
};

/**
 * 한 종류의 그릴 수 있는 시점을 전부 켜거나 끈 새 초안 ("모두 켜기" / "모두 끄기" 텍스트 버튼).
 * 초안만 바꾼다. 저장은 따로 누른다(7-3).
 */
export const setDraftKindAll = (
    draft: MemberNotificationSettingsDraft,
    settings: MemberNotificationSettings,
    kind: MemberNotificationKind,
    value: boolean,
): MemberNotificationSettingsDraft => {
    const next = { ...draft[kind] };
    getRenderableTimings(settings, kind).forEach((timing) => {
        next[timing] = value;
    });
    return { ...draft, [kind]: next };
};

// ─────────────────────────────────────────────────────────────
// diff → PATCH 본문
// ─────────────────────────────────────────────────────────────

/**
 * 서버 상태와 **다른 키만** 담은 PATCH 본문. 바뀐 것이 없으면 **null** (= 저장 비활성).
 *
 * - 그릴 수 있는 키(응답 ∩ 사전)만 비교한다. 서버가 모르는 키는 절대 싣지 않는다(400 방지).
 * - 초안 값이 불리언이 아니면 싣지 않는다(Joi `.strict()`).
 * - 바뀐 키가 없는 종류는 섹션째 뺀다(Joi `.min(1)`).
 */
export const buildMemberNotificationSettingsPatch = (
    settings: MemberNotificationSettings,
    draft: MemberNotificationSettingsDraft,
): MemberNotificationSettingsPatchBody | null => {
    const body: MemberNotificationSettingsPatchBody = {};
    MEMBER_NOTIFICATION_KINDS.forEach((kind) => {
        const changed: MemberNotificationTimingFlags = {};
        getRenderableTimings(settings, kind).forEach((timing) => {
            const next = draft[kind]?.[timing];
            if (typeof next === 'boolean' && next !== settings[kind][timing]) changed[timing] = next;
        });
        if (Object.keys(changed).length > 0) body[kind] = changed;
    });
    return Object.keys(body).length > 0 ? body : null;
};

/**
 * 보내기 직전 본문을 한 번 더 거른다 — 훅의 `save` 가 부른다.
 *
 * `buildMemberNotificationSettingsPatch` 를 거치지 않고 손으로 만든 본문이 들어와도
 * 그릴 수 있는 키(응답 ∩ 사전)의 불리언 값만 남긴다. 서버가 모르는 키 하나가
 * **요청 전체를 400 으로** 만들기 때문이다(4-4). 남는 것이 없으면 null.
 * 값이 서버와 같은 키는 빼지 않는다(같은 값 재저장은 서버에서 멱등, 4-6).
 */
export const sanitizeMemberNotificationSettingsPatch = (
    settings: MemberNotificationSettings,
    body: MemberNotificationSettingsPatchBody | null | undefined,
): MemberNotificationSettingsPatchBody | null => {
    if (!isPlainObject(body)) return null;
    const clean: MemberNotificationSettingsPatchBody = {};
    MEMBER_NOTIFICATION_KINDS.forEach((kind) => {
        const section = body[kind];
        if (!isPlainObject(section)) return;
        const kept: MemberNotificationTimingFlags = {};
        getRenderableTimings(settings, kind).forEach((timing) => {
            if (typeof section[timing] === 'boolean') kept[timing] = section[timing] as boolean;
        });
        if (Object.keys(kept).length > 0) clean[kind] = kept;
    });
    return Object.keys(clean).length > 0 ? clean : null;
};

/** 변경이 있는가 — 저장 버튼 활성, "저장하지 않고 닫을까요?" 판정에 쓴다. */
export const hasMemberNotificationSettingsChanges = (
    settings: MemberNotificationSettings,
    draft: MemberNotificationSettingsDraft,
) => buildMemberNotificationSettingsPatch(settings, draft) !== null;

// ─────────────────────────────────────────────────────────────
// 종류별 상태 (모두 보냄 / 일부만 보냄 / 보내지 않음)
// ─────────────────────────────────────────────────────────────

export type MemberNotificationKindSendState = 'all' | 'partial' | 'none';

/**
 * 한 종류의 상태. `values` 에 서버 상태나 초안 어느 쪽이든 넣을 수 있다
 * (배지는 보통 초안으로 계산한다 — 저장 전에도 누른 결과가 보여야 한다).
 *
 * 계산 대상은 `getRenderableTimings(settings, kind)` — 화면에 보이는 행만이다.
 * 그릴 행이 하나도 없으면 **null** (배지를 그리지 않는다).
 */
export const getKindSendState = (
    settings: MemberNotificationSettings,
    values: Pick<MemberNotificationSettingsDraft, MemberNotificationKind>,
    kind: MemberNotificationKind,
): MemberNotificationKindSendState | null => {
    const timings = getRenderableTimings(settings, kind);
    if (timings.length === 0) return null;
    const onCount = timings.filter((timing) => values[kind]?.[timing] === true).length;
    if (onCount === timings.length) return 'all';
    if (onCount === 0) return 'none';
    return 'partial';
};

// ─────────────────────────────────────────────────────────────
// 한 종류 전부 끄기 판정 (확인창)
// ─────────────────────────────────────────────────────────────

/**
 * 이번 저장이 이 종류를 **전부 꺼진 상태로 만드는가** — 확인창을 띄울지 정한다(7-3).
 *
 * 참이 되는 조건 (둘 다):
 *   ① 저장 후 이 종류가 '보내지 않음' (그릴 수 있는 시점이 전부 false)
 *   ② 이번 PATCH 가 이 종류의 키를 하나 이상 바꾼다
 *
 * ②가 필요한 이유: 이미 전부 꺼져 저장된 종류는 다른 종류만 바꿔 저장할 때
 * "모두 끌까요?" 를 다시 묻지 않는다 — 이번 저장이 끄는 것이 아니다.
 */
export const isTurningKindAllOff = (
    settings: MemberNotificationSettings,
    draft: MemberNotificationSettingsDraft,
    kind: MemberNotificationKind,
): boolean => {
    if (getKindSendState(settings, draft, kind) !== 'none') return false;
    const body = buildMemberNotificationSettingsPatch(settings, draft);
    return !!body?.[kind];
};

/** 이번 저장으로 전부 꺼지는 종류들 (표시 순서). 비어 있으면 확인창 없이 저장한다. */
export const getKindsTurningAllOff = (
    settings: MemberNotificationSettings,
    draft: MemberNotificationSettingsDraft,
): MemberNotificationKind[] => MEMBER_NOTIFICATION_KINDS.filter((kind) => isTurningKindAllOff(settings, draft, kind));

// ─────────────────────────────────────────────────────────────
// 결과 분류 (7-5)
// ─────────────────────────────────────────────────────────────

/** 요청을 보내지 않고 훅에서 막은 이유 */
export type MemberNotificationSettingsClientBlockReason = 'READ_ONLY' | 'NOT_READY' | 'EMPTY' | 'IN_FLIGHT';

export type MemberNotificationSettingsResultKind =
    | 'ok' // 200
    | 'blocked' // 요청을 보내지 않음 (clientBlocked)
    | 'network' // 응답 없음 (status 0)
    | 'badRequest' // 400
    | 'unauthorized' // 401 — 전역 인터셉터가 세션 만료 처리
    | 'viewOnly' // 403 VIEW_ONLY — 열람 모드 쓰기. 인터셉터가 알럿을 띄운다
    | 'forbidden' // 403 (다른 센터 admin, superAdmin PATCH, 열람 세션의 다른 센터)
    | 'notFound' // 404 (없는·삭제된 센터 / 이 API 가 없는 구버전 백엔드)
    | 'notAcceptable' // 406 (coach·member)
    | 'conflict' // 409 (4-2 표에는 없다. 오면 구분만 해 둔다)
    | 'server' // 5xx
    | 'unknown'; // 그 밖

export type MemberNotificationSettingsResultClass = {
    kind: MemberNotificationSettingsResultKind;
    /** HTTP 상태. 응답이 없으면 0 */
    status: number;
    /** 서버/인터셉터 본문의 `code` (예: 'VIEW_ONLY', 'CENTER_SCOPE_VIOLATION') */
    code: string | null;
    /** kind === 'blocked' 일 때만 */
    blockReason: MemberNotificationSettingsClientBlockReason | null;
};

/** `{status, data}` → 분기용 분류. 화면은 status 숫자 대신 `kind` 로 분기한다. */
export const classifyMemberNotificationSettingsResult = (
    result: MemberNotificationSettingsApiResult | null | undefined,
): MemberNotificationSettingsResultClass => {
    const status = typeof result?.status === 'number' ? result.status : 0;
    const data = result?.data;
    const code = isPlainObject(data) && typeof data.code === 'string' ? data.code : null;
    const blockReason =
        isPlainObject(data) && typeof data.clientBlocked === 'string'
            ? (data.clientBlocked as MemberNotificationSettingsClientBlockReason)
            : null;
    const make = (kind: MemberNotificationSettingsResultKind): MemberNotificationSettingsResultClass => ({
        kind,
        status,
        code,
        blockReason: kind === 'blocked' ? blockReason : null,
    });

    if (status === 200) return make('ok');
    if (status === 0) return make(blockReason ? 'blocked' : 'network');
    if (status === 400) return make('badRequest');
    if (status === 401) return make('unauthorized');
    if (status === 403) return make(code === 'VIEW_ONLY' ? 'viewOnly' : 'forbidden');
    if (status === 404) return make('notFound');
    if (status === 406) return make('notAcceptable');
    if (status === 409) return make('conflict');
    if (status >= 500 && status <= 599) return make('server');
    return make('unknown');
};

/**
 * 저장 실패 → 인라인 배너 문구(7-5). 배너를 띄우지 않을 경우 null.
 *
 * - ok: 성공 문구는 `MEMBER_NOTIFICATION_STATUS_COPY.saved` 를 따로 쓴다
 * - blocked: 요청을 보내지 않았다. 화면이 이미 비활성이어야 하는 상황이다
 * - viewOnly / unauthorized: 전역 axios 인터셉터가 알럿·세션 만료를 처리한다(중복 표시 방지)
 *
 * 실패해도 **초안은 유지**한다(7-5) — 이 함수는 문구만 정한다.
 */
export const getMemberNotificationSaveFailureMessage = (
    result: MemberNotificationSettingsResultClass,
): string | null => {
    switch (result.kind) {
        case 'ok':
        case 'blocked':
        case 'viewOnly':
        case 'unauthorized':
            return null;
        case 'forbidden':
        case 'notAcceptable':
            return MEMBER_NOTIFICATION_STATUS_COPY.save.forbidden;
        case 'notFound':
            return MEMBER_NOTIFICATION_STATUS_COPY.save.notFound;
        default:
            return MEMBER_NOTIFICATION_STATUS_COPY.save.error;
    }
};

// ─────────────────────────────────────────────────────────────
// 화면 단계 (규칙 15)
// ─────────────────────────────────────────────────────────────

/**
 * 모달이 그릴 단계.
 * - `loading`     스피너. **토글을 그리지 않는다.**
 * - `ready`       토글·저장 버튼을 그려도 된다. `settings` 가 있다.
 * - `unavailable` GET 404 — "지금은 이 설정을 사용할 수 없어요." + 다시 시도
 * - `forbidden`   GET 403/406 — "센터 대표 계정에서만 볼 수 있어요."
 * - `error`       그 밖·네트워크·응답 모양 이상 — "설정을 불러오지 못했어요." + 다시 시도
 */
export type MemberNotificationSettingsPhase = 'loading' | 'ready' | 'unavailable' | 'forbidden' | 'error';

export type MemberNotificationSettingsView = {
    phase: MemberNotificationSettingsPhase;
    /** phase === 'ready' 일 때만 값이 있다 */
    settings: MemberNotificationSettings | null;
    /** 마지막으로 끝난 GET 의 분류. 아직 끝난 GET 이 없으면 null */
    loadResult: MemberNotificationSettingsResultClass | null;
};

/**
 * React Query 상태 → 화면 단계. 훅이 쓰는 순수 함수다.
 *
 * `ready` 는 **이번 모달 열림에서 시작한 GET 이 200 으로 끝났고, 지금 진행 중인 GET 이 없을 때**만이다.
 * "이번 열림에서 시작한" 은 호출부(훅)가 쿼리 키에 열림마다 새 id 를 넣어 보장한다 —
 * 여기 들어오는 `data` 는 애초에 이번 열림의 요청 결과뿐이다.
 */
export const deriveMemberNotificationSettingsView = ({
    hasCenterId,
    queryStatus,
    isFetching,
    data,
}: {
    hasCenterId: boolean;
    queryStatus: 'loading' | 'error' | 'success';
    isFetching: boolean;
    data: MemberNotificationSettingsApiResult | undefined;
}): MemberNotificationSettingsView => {
    if (!hasCenterId) {
        return { phase: 'error', settings: null, loadResult: null };
    }
    // queryFn 은 던지지 않는다. 그래도 던졌다면(코드 결함) 불러오지 못한 것으로 본다.
    if (queryStatus === 'error' && !isFetching) {
        return { phase: 'error', settings: null, loadResult: classifyMemberNotificationSettingsResult(null) };
    }
    if (queryStatus !== 'success' || isFetching || !data) {
        return { phase: 'loading', settings: null, loadResult: null };
    }

    const loadResult = classifyMemberNotificationSettingsResult(data);
    switch (loadResult.kind) {
        case 'ok': {
            const settings = parseMemberNotificationSettings(data.data);
            return settings
                ? { phase: 'ready', settings, loadResult }
                : { phase: 'error', settings: null, loadResult };
        }
        case 'notFound':
            return { phase: 'unavailable', settings: null, loadResult };
        case 'forbidden':
        case 'notAcceptable':
            return { phase: 'forbidden', settings: null, loadResult };
        default:
            return { phase: 'error', settings: null, loadResult };
    }
};
